import React from "react";
import {BaseComponent} from "@renta-apps/athenaeum-react-common";
import PageTotals from "../PageTotals/PageTotals";
import AllPagesTotals from "../AllPagesTotals/AllPagesTotals";
import {ITotalsLayout} from "./TotalsLayout";
import UserSalaryHoursTotals from "../../../../models/server/UserSalaryHoursTotals";

import styles from "./TotalsTable.module.scss";

interface ITotalsTableProps {
    /**
     * The horizontal scroll container of the grid. The totals table must be rendered as its child (after the grid), so both scroll together.
     */
    containerRef: React.RefObject<HTMLElement>;

    /**
     * Id of the <table> element rendered by the grid (athenaeum renders it as "table_" + grid id).
     */
    gridTableId: string;

    /**
     * Accessors of the grid's visible columns in display order (null for columns without an accessor), or null when the grid is not ready.
     */
    getVisibleAccessors(): (string | null)[] | null;

    pageTotalsRef: React.RefObject<PageTotals>;

    allPagesTotalsRef: React.RefObject<AllPagesTotals>;

    fetchTotals(): Promise<UserSalaryHoursTotals>;
}

interface ITotalsTableState {
    layout: ITotalsLayout | null;
}

const emptyLayout: ITotalsLayout = {offsetLeft: 0, widths: [], accessors: []};

/**
 * "Total (this page)" and "Total (all pages)" rows of the Daily hours report, shown in their own table after the grid in the DOM;
 * the container's flex order places them between the grid rows and its pagination.
 *
 * The athenaeum Grid has no hook for custom rows, so the column widths are measured (read only) from the grid's rendered header cells
 * and applied to this table. They are measured again whenever they can change: when the container or any measured header cell is resized,
 * and when the grid's DOM changes (data reload, paging, page size, sorting, language, visible columns).
 */
export default class TotalsTable extends BaseComponent<ITotalsTableProps, ITotalsTableState> {

    state: ITotalsTableState = {
        layout: null,
    };

    private readonly _tableRef: React.RefObject<HTMLTableElement> = React.createRef();
    private _resizeObserver: ResizeObserver | null = null;
    private _mutationObserver: MutationObserver | null = null;
    private _observedElements: Element[] = [];
    private _syncFrame: number = 0;

    private static areClose(a: number, b: number): boolean {
        return Math.abs(a - b) < 0.01;
    }

    private static isSameLayout(a: ITotalsLayout | null, b: ITotalsLayout | null): boolean {
        if ((a == null) || (b == null)) {
            return (a === b);
        }

        return (TotalsTable.areClose(a.offsetLeft, b.offsetLeft)) &&
            (a.widths.length === b.widths.length) &&
            (a.widths.every((width, index) => TotalsTable.areClose(width, b.widths[index]))) &&
            (a.accessors.length === b.accessors.length) &&
            (a.accessors.every((accessor, index) => accessor === b.accessors[index]));
    }

    /**
     * Header cells of the leaf columns in display order. With header groups (e.g. "Overtime") the header has two rows: a leaf cell is
     * either a cell of the last header row, or a first-row cell that spans every header row.
     */
    private static getLeafHeaderCells(gridTable: HTMLTableElement): HTMLTableCellElement[] {
        const headerRows: HTMLTableRowElement[] = Array.from(gridTable.tHead?.rows ?? []);
        const leaves: HTMLTableCellElement[] = [];

        headerRows.forEach((row, rowIndex) => {
            Array.from(row.cells).forEach(cell => {
                if (rowIndex + Math.max(cell.rowSpan, 1) >= headerRows.length) {
                    leaves.push(cell);
                }
            });
        });

        return leaves.sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);
    }

    private scheduleSync(): void {
        cancelAnimationFrame(this._syncFrame);
        this._syncFrame = requestAnimationFrame(() => this.syncLayoutAsync());
    }

    /**
     * (Re)attaches the resize observer when the set of measured elements changed. Observing an element reports it once right away,
     * so re-observing the same elements on every sync would loop.
     */
    private observe(elements: Element[]): void {
        const observer: ResizeObserver | null = this._resizeObserver;
        if (!observer) {
            return;
        }

        const same: boolean = (elements.length === this._observedElements.length) &&
            (elements.every((element, index) => element === this._observedElements[index]));

        if (!same) {
            observer.disconnect();
            elements.forEach(element => observer.observe(element));
            this._observedElements = elements;
        }
    }

    private measureLayout(): ITotalsLayout | null {
        const container: HTMLElement | null = this.props.containerRef.current;
        const gridTable: HTMLElement | null = document.getElementById(this.props.gridTableId);

        if ((!container) || (!(gridTable instanceof HTMLTableElement)) || (!container.contains(gridTable))) {
            this.observe(container ? [container] : []);
            return null;
        }

        const leaves: HTMLTableCellElement[] = TotalsTable.getLeafHeaderCells(gridTable);
        const accessors: (string | null)[] | null = this.props.getVisibleAccessors();

        this.observe([container, gridTable, ...leaves]);

        // Every leaf header cell must map to exactly one visible column; otherwise the header is not in a state we can align to.
        if ((accessors == null) || (leaves.length === 0) || (leaves.length !== accessors.length) || (leaves.some(cell => cell.colSpan > 1))) {
            return null;
        }

        const rects: DOMRect[] = leaves.map(cell => cell.getBoundingClientRect());
        const containerRect: DOMRect = container.getBoundingClientRect();
        const contentLeft: number = containerRect.left + container.clientLeft - container.scrollLeft;

        // Widths are the distances between neighbouring column edges, so the cumulative position of each totals cell equals its header's.
        const widths: number[] = rects.map((rect, index) => (index < rects.length - 1)
            ? rects[index + 1].left - rect.left
            : rect.right - rect.left);

        return {
            offsetLeft: rects[0].left - contentLeft,
            widths,
            accessors,
        };
    }

    private async syncLayoutAsync(): Promise<void> {
        if (!this.isMounted) {
            return;
        }

        const layout: ITotalsLayout | null = this.measureLayout();

        if (!TotalsTable.isSameLayout(layout, this.state.layout)) {
            await this.setState({layout});
        }
    }

    public async componentDidMount(): Promise<void> {
        await super.componentDidMount();

        this._resizeObserver = new ResizeObserver(() => this.scheduleSync());

        const container: HTMLElement | null = this.props.containerRef.current;
        if (container) {
            // Grid re-renders (reload, paging, sorting, language, columns) change its DOM. Changes inside this table are ignored,
            // so applying a new layout does not trigger another measurement.
            this._mutationObserver = new MutationObserver((records: MutationRecord[]) => {
                const table: HTMLTableElement | null = this._tableRef.current;
                if (records.some(record => (!table) || (!table.contains(record.target)))) {
                    this.scheduleSync();
                }
            });
            this._mutationObserver.observe(container, {subtree: true, childList: true, characterData: true});
        }

        this.scheduleSync();
    }

    public async componentWillUnmount(): Promise<void> {
        await super.componentWillUnmount();

        cancelAnimationFrame(this._syncFrame);
        this._mutationObserver?.disconnect();
        this._mutationObserver = null;
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
        this._observedElements = [];
    }

    public render(): React.ReactNode {
        const layout: ITotalsLayout | null = this.state.layout;
        const synced: boolean = (layout != null);
        const appliedLayout: ITotalsLayout = layout ?? emptyLayout;
        const tableWidth: number = appliedLayout.widths.reduce((sum, width) => sum + width, 0);

        // The rows stay mounted while the layout is not measured yet: the owner pushes page totals into them and the all pages row loads itself.
        return (
            <table id="workDayTotals"
                   ref={this._tableRef}
                   className={this.css(styles.totalsTable, (!synced) && styles.notSynced)}
                   style={(synced) ? {width: `${tableWidth}px`, marginLeft: `${appliedLayout.offsetLeft}px`} : undefined}
                   data-synced={String(synced)}
            >

                <colgroup>
                    {
                        appliedLayout.widths.map((width, index) => (
                            <col key={index} style={{width: `${width}px`}}/>
                        ))
                    }
                </colgroup>

                <tbody>

                    <PageTotals ref={this.props.pageTotalsRef}
                                layout={appliedLayout}
                    />

                    <AllPagesTotals ref={this.props.allPagesTotalsRef}
                                    layout={appliedLayout}
                                    fetchTotals={this.props.fetchTotals}
                    />

                </tbody>

            </table>
        );
    }
}
